import React, { forwardRef, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  Slide,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  Fab,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Close, DeveloperMode } from "@mui/icons-material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object } from "yup";
import { socialDataAndChargesSchema } from "./schema/socialDataAndChargesSchema";
import { admissionSchema } from "./schema/admissionSchema";
import { payrollSchema } from "./schema/payrollSchema";
import { bankCommunicationSchema } from "./schema/bankCommunicationSchema";
import { businessRuleSchema } from "./schema/businessRuleSchema";
import { benefitsSchema } from "./schema/benefitsSchema";
import SocialDataAndCharges from "./components/SocialDataAndCharges";
import Admission from "./components/Admission";
import Payroll from "./components/Payroll";
import BankCommunication from "./components/BankCommunication";
import BusinessRule from "./components/BusinessRule";
import Benefits from "./components/Benefits";
import OnboardingChat from "./components/OnboardingChat";
import { demoValues } from "./demoValues";
import { useAuth } from "../../../../contexts/AuthContext";
import {
  useSaveOnboarding,
  useSendOnboarding,
  useReturnValidationOnboarding,
  useCloseVersionOnboarding,
} from "./hooks/useOnboarding";
import { useGetOnboarding } from "./hooks/useGetOnboarding";
import { getUsers } from "../../../../services/vmApi";
import Autocomplete from "../../../../components/react-form/Autocomplete";
import { useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";

const APP_STAGE = process.env.REACT_APP_STAGE;

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const tabs = [
  "Dados sociais & encargos",
  "Admissão",
  "Folha de pagamento",
  "Comunicação bancária",
  "Regra de negócio",
  "Benefício",
];

const allSchemas = [
  socialDataAndChargesSchema,
  admissionSchema,
  payrollSchema,
  bankCommunicationSchema,
  businessRuleSchema,
  benefitsSchema,
];

const combinedSchema = object().shape(
  allSchemas.reduce((acc, schema) => ({ ...acc, ...schema.fields }), {}),
);

const defaultValues = {
  socialDataAndChargesIsValidated: false,
  companyCnpj: "",
  companyName: "",
  taxRegime: "",
  mainCnae: "",
  companyHasAgreements: "",
  emitBoletoSeparately: "",
  isEmpresaCidada: "",
  hasCollectiveVacation: "",
  collectiveVacationPeriod: [],
  collectiveVacationDays: "",
  collectiveVacationByCostCenter: "",
  collectiveVacationOtherRule: "",
  admissionIsValidated: false,
  trialContractTerm: "",
  trialContractTermOther: "",
  hasSpecificRegistrationRules: "",
  specificRegistrationRulesDescription: "",
  useSamePayrollRegistrationForTimeTracking: "",
  workTimeControl: "",
  timeTrackingClosePeriod: "",
  timeTrackingClosePeriodOther: "",
  hasBankOfHours: "",
  bankOfHoursFrequency: "",
  bankOfHoursCompetencies: [],
  payrollIsValidated: false,
  calendarDefaultValues: [],
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].reduce((acc, id) => {
    acc[`notApplicable-${id}`] = false;
    acc[`paymentDay-${id}`] = "";
    return acc;
  }, {}),
  anticipatePaymentsOnWeekendsHolidays: "",
  anticipatePaymentsDescription: "",
  hasMonthlyAdvance: "",
  advancePercentage: "",
  advanceCalcBase: "",
  advanceCalcBaseForCommissioned: "",
  advanceCalcBaseForCommissionedOther: "",
  minDaysWorkedForAdvance: "",
  advancePaymentCalculationType: "",
  newHiresReceiveAdvance: "",
  advanceAdhesionNextMonth: "",
  maternityLeaveReceiveAdvance: "",
  proLaboreReceiveAdvance: "",
  internReceiveAdvance: "",
  apprenticeReceiveAdvance: "",
  irrfAdvanceRetentionRule: "",
  irrfAdvanceRetentionRuleOther: "",
  advanceAnalyticalReportOrder: "",
  advanceNetReport: "",
  advanceNetReportExcel: "",
  thirteenthFirstInstallmentWriteOff: "",
  vacationWriteOffMethod: "",
  resignationProvisionWhenTerminated: "",
  provisionAnalyticalReportOrder: "",
  provisionSyntheticReportOrder: "",
  provisionAccountingSyntheticReportOrder: "",
  thirteenthAdvanceOnBirthday: "",
  thirteenthFirstInstallmentWithAverages: "",
  thirteenthFirstInstallmentWithAveragesOther: "",
  internProLaboreThirteenth: "",
  internProLaboreThirteenthRubricsNovDec: "",
  internProLaboreThirteenthRubricsDecReceive: "",
  internProLaboreThirteenthRubricsDecOnly: "",
  internProLaboreThirteenthOtherRule: "",
  thirteenthAnalyticalReportOrder: "",
  thirteenthSyntheticReportOrder: "",
  thirteenthNetReport: "",
  thirteenthNetReportExcel: "",
  hasPlr: "",
  plrAccordingToCctAct: "",
  plrBusinessRule: "",
  plrFrequencyAndCompetencies: "",
  plrSameCalendarYear: [],
  plrAnalyticalReportOrder: "",
  plrSyntheticReportOrder: "",
  plrNetReport: "",
  plrNetReportExcel: "",
  plrAccountingSyntheticReportOrder: "",
  hasConsignedLoan: "",
  consignedLoanDeductionLaw: "",
  consignedLoanDeductionOther: "",
  consignedLoanFullDeductionIfNoBalance: "",
  consignedLoanFullDeductionIfNoBalanceOther: "",
  consignedLoanFormat: "",
  consignedLoanDataReceptionDate: "",
  bankCommunicationIsValidated: false,
  hasBankTransferFile: "",
  bankFileLayoutD1: "",
  bankSequentialNumberRule: "",
  issueUnifiedBankFile: "",
  bankAccounts: [
    {
      cnpj: "",
      bank: "",
      agency: "",
      account: "",
      agreementCode: "",
    },
  ],
  specificPaymentRule: "",
  specificPaymentRuleDescription: "",
  businessRuleIsValidated: false,
  collectiveAgreementType: "",
  applyCollectiveAgreementRules: "",
  collectiveAgreementPartialClauses: "",
  applyCollectiveAgreementRulesOther: "",
  vacationAdvanceDiscounts: "",
  vacationAdvanceDiscountsList: "",
  vacationDiscountConditions: "",
  vacationAverages: "",
  vacationAveragesOther: "",
  vacationBenefits: "",
  vacationBenefitsList: "",
  vacationBenefitsDescription: "",
  terminationDiscounts: [],
  terminationDiscountsOther: "",
  loanDiscountPercentageTermination: "",
  loanDiscountPercentageTerminationOther: "",
  terminationAverages: "",
  terminationAveragesOther: "",
  payrollHasInterns: "",
  internSalaryAdjustmentRule: "",
  internSalaryAdjustmentOther: "",
  internHasDeductions: "",
  internDeductionsDescription: "",
  internHasBenefits: "",
  interBenefitsCalculation: "",
  interPayrollProcessing: "",
  internFixedBenefit: "",
  internFixedBenefitRubric: "",
  internFixedBenefitRule: "",
  internBenefitValue: "",
  internProportionalCalcRule: "",
  internProportionalCalcRuleOther: "",
  payrollHasApprentices: "",
  apprenticeSalaryAdjustmentRule: "",
  apprenticeSalaryAdjustmentOther: "",
  apprenticeHasDeductions: "",
  apprenticeDeductionsDescription: "",
  apprenticeHasBenefits: "",
  apprenticeBenefitsCalculation: "",
  apprenticePayrollProcessing: "",
  apprenticeFixedBenefit: "",
  apprenticeFixedBenefitRubric: "",
  apprenticeFixedBenefitRule: "",
  apprenticeBenefitValue: "",
  apprenticeProportionalCalcRule: "",
  apprenticeProportionalCalcRuleOther: "",
  hasAutonomous: "",
  autonomousBusinessRule: "",
  hasProLabore: "",
  proLaboreSalaryAdjustmentRule: "",
  proLaboreSalaryAdjustmentOther: "",
  proLaboreHasFgtsNoLink: "",
  payrollAnalyticalReportOrder: "",
  payrollSyntheticReportOrder: "",
  payrollNetReport: "",
  payrollNetReportExcel: "",
  payrollAccountingSyntheticReportOrder: "",
  hasDomesticEmployee: "",
  domesticEmployeeSalaryAdjustmentRule: "",
  domesticEmployeeSalaryAdjustmentRuleOther: "",
  domesticEmployeeWorkTimeControl: "",
  domesticEmployeeClosePeriod: "",
  domesticEmployeeClosePeriodOther: "",
  hasSpecificWageItems: "",
  specificWageRows: {},
  hasCustomWageIncidences: "",
  customWageRows: {},
  hasHazardPay: "",
  hasInsalubrityPay: "",
  discountAdditionalOnAbsence: "",
  proportionalRuleForDifferentMonthDays: "",
  otherExclusiveRules: "",
  otherExclusiveRulesDescription: "",
  benefitsIsValidated: false,
  hasLifeInsurance: "",
  lifeInsuranceMovementType: "",
  lifeInsuranceMovementReport: "",
  lifeInsuranceMovementReportDeadline: "",
  lifeInsuranceDiscountValue: "",
  lifeInsuranceDiscountValueFix: "",
  lifeInsuranceDiscountValueSalaryPercentage: "",
  lifeInsuranceDiscountValueOther: "",
  lifeInsuranceCompanyCost: "",
  lifeInsuranceEmployeeCost: "",
  lifeInsuranceProLabore: "",
  lifeInsuranceIntern: "",
  lifeInsuranceApprentice: "",
  lifeInsuranceOnLeave: "",
  lifeInsuranceDiscountOnPayroll: "",
  lifeInsuranceDiscountOnPayrollOther: "",
  lifeInsuranceDiscountInTermination: "",
  lifeInsuranceOtherRules: "",
  lifeInsuranceOtherRulesDescription: "",
  hasHealthPlan: "",
  healthPlanOperators: [
    {
      name: "",
      cnpj: "",
      ansCode: "",
      type: "",
      companyCost: "",
      employeeCost: "",
      dependentCost: "",
      aggregateCost: "",
      hasCoparticipation: "",
      companyCoparticipationCost: "",
      employeeCoparticipationCost: "",
      dependentCoparticipationCost: "",
      aggregateCoparticipationCost: "",
      coparticipationRule: "",
      coparticipationRuleDescription: "",
      beneficiariesOutsideEmployees: "",
      beneficiariesOutsideEmployeesDescription: "",
      reportReceptionDate: "",
      adjustmentDate: "",
    },
  ],
  healthPlanDiscountOnPayroll: "",
  healthPlanDiscountOnPayrollOther: "",
  healthPlanDiscountInTermination: "",
  healthPlanOtherRules: "",
  healthPlanOtherRulesDescription: "",
  hasDentalPlan: "",
  dentalPlanOperators: [
    {
      name: "",
      cnpj: "",
      ansCode: "",
      type: "",
      companyCost: "",
      employeeCost: "",
      dependentCost: "",
      aggregateCost: "",
      beneficiariesOutsideEmployees: "",
      beneficiariesOutsideEmployeesDescription: "",
      reportReceptionDate: "",
      adjustmentDate: "",
    },
  ],
  dentalPlanDiscountOnPayroll: "",
  dentalPlanDiscountOnPayrollOther: "",
  dentalPlanDiscountInTermination: "",
  dentalPlanOtherRules: "",
  dentalPlanOtherRulesDescription: "",
  hasTransportationVoucher: "",
  transportationOperatorName: "",
  transportationCalculationPeriod: "",
  transportationDeductionPeriod: "",
  transportationCalcExecution: "",
  transportationPurchaseDeadline: "",
  transportationDeductAbsences: "",
  transportationDeductMedicalCertificates: "",
  transportationBenefitForAccidentLeave: "",
  transportationAccidentLeaveLimit: "",
  transportationBenefitForAccidentLeaveOther: "",
  transportationBenefitForIllnessLeave: "",
  transportationBenefitForIllnessLeaveLimit: "",
  transportationBenefitForIllnessLeaveOther: "",
  transportationBenefitForMaternityLeave: "",
  transportationBenefitForMaternityLeaveLimit: "",
  transportationBenefitForMaternityLeaveOther: "",
  transportationBenefitDuringVacation: "",
  transportationOtherDeductionFactors: "",
  transportationDeductionRuleDescription: "",
  transportationPayrollDeduction: "",
  transportationPayrollDeductionOther: "",
  transportationTerminationDeduction: "",
  transportationUnusedBenefitDeduction: "",
  hasMobilityVoucher: "",
  mobilityOperatorName: "",
  mobilityCalculationPeriod: "",
  mobilityDeductionPeriod: "",
  mobilityCalcExecution: "",
  mobilityPurchaseDeadline: "",
  mobilityDeductAbsences: "",
  mobilityDeductMedicalCertificates: "",
  mobilityBenefitForAccidentLeave: "",
  mobilityAccidentLeaveLimit: "",
  mobilityBenefitForAccidentLeaveOther: "",
  mobilityBenefitForIllnessLeave: "",
  mobilityBenefitForIllnessLeaveLimit: "",
  mobilityBenefitForIllnessLeaveOther: "",
  mobilityBenefitForMaternityLeave: "",
  mobilityBenefitForMaternityLeaveLimit: "",
  mobilityBenefitForMaternityLeaveOther: "",
  mobilityBenefitDuringVacation: "",
  mobilityOtherDeductionFactors: "",
  mobilityDeductionRuleDescription: "",
  mobilityPayrollDeduction: "",
  mobilityPayrollDeductionOther: "",
  mobilityTerminationDeduction: "",
  mobilityUnusedBenefitDeduction: "",
  hasFoodVoucher: "",
  foodOperatorName: "",
  foodCalculationPeriod: "",
  foodDeductionPeriod: "",
  foodCalcExecution: "",
  foodPurchaseDeadline: "",
  foodDeductAbsences: "",
  foodDeductMedicalCertificates: "",
  foodBenefitForAccidentLeave: "",
  foodAccidentLeaveLimit: "",
  foodBenefitForAccidentLeaveOther: "",
  foodBenefitForIllnessLeave: "",
  foodBenefitForIllnessLeaveLimit: "",
  foodBenefitForIllnessLeaveOther: "",
  foodBenefitForMaternityLeave: "",
  foodBenefitForMaternityLeaveLimit: "",
  foodBenefitForMaternityLeaveOther: "",
  foodBenefitDuringVacation: "",
  foodOtherDeductionFactors: "",
  foodDeductionRuleDescription: "",
  foodPayrollDeduction: "",
  foodPayrollDeductionOther: "",
  foodTerminationDeduction: "",
  foodUnusedBenefitDeduction: "",
  hasMealVoucher: "",
  mealOperatorName: "",
  mealCalculationPeriod: "",
  mealDeductionPeriod: "",
  mealCalcExecution: "",
  mealPurchaseDeadline: "",
  mealDeductAbsences: "",
  mealDeductMedicalCertificates: "",
  mealBenefitForAccidentLeave: "",
  mealAccidentLeaveLimit: "",
  mealBenefitForAccidentLeaveOther: "",
  mealBenefitForIllnessLeave: "",
  mealBenefitForIllnessLeaveLimit: "",
  mealBenefitForIllnessLeaveOther: "",
  mealBenefitForMaternityLeave: "",
  mealBenefitForMaternityLeaveLimit: "",
  mealBenefitForMaternityLeaveOther: "",
  mealBenefitDuringVacation: "",
  mealOtherDeductionFactors: "",
  mealDeductionRuleDescription: "",
  mealPayrollDeduction: "",
  mealPayrollDeductionOther: "",
  mealTerminationDeduction: "",
  mealUnusedBenefitDeduction: "",
  hasBasicBasket: "",
  basicBasketProvider: "",
  basicBasketCalculationPeriod: "",
  basicBasketBusinessRule: "",
  basicBasketPayrollDeduction: "",
  basicBasketPayrollDeductionOther: "",
  basicBasketTerminationDeduction: "",
  basicBasketUnusedBenefitDeduction: "",
  hasCanteen: "",
  canteenCalculationPeriod: "",
  canteenBusinessRule: "",
  canteenPayrollDeduction: "",
  canteenPayrollDeductionOther: "",
  canteenTerminationDeduction: "",
  canteenUnusedBenefitDeduction: "",
  hasOtherBenefits: "",
  otherBenefits: [{ description: "", businessRule: "" }],
  hasPharmacy: "",
  agreements: [
    {
      description: "",
      calculationPeriod: "",
      infoFormat: "",
      reportDeadline: "",
      payrollDeduction: "",
      payrollDeductionOther: "",
      terminationDeduction: "",
      unusedBenefitDeduction: "",
    },
  ],
  hasSocialSecurity: "",
  socialSecurityUser: "",
  socialSecurityPassword: "",
  hasPowerOfAttorney: "",
  eSocialPowerOfAttorneyExpirationDate: "",
  connectivityPowerOfAttorneyExpirationDate: "",
  employerWebPowerOfAttorneyExpirationDate: "",
};

export default function Registration() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [schemaErrors, setSchemaErrors] = useState([]);
  const [responsibleUser, setResponsibleUser] = useState(null);
  const [responsibleQuery, setResponsibleQuery] = useState("");
  const [chatEnabled, setChatEnabled] = useState(false);
  const [editable, setEditable] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [isCloseVersionDialogOpen, setIsCloseVersionDialogOpen] = useState(false);

  const companyCode = searchParams.get("company_code") || "";

  const isStaff = user?.groups
    ?.map(group => group.name)
    ?.includes("analista-folha");

  const responsibleUsersQuery = useInfiniteQuery(
    ["onboarding-responsible-users", responsibleQuery],
    ({ pageParam = 1 }) =>
      getUsers({
        query: responsibleQuery,
        pageIndex: pageParam,
        onlyActive: true,
        inGroup: "analista-folha",
      }),
    {
      getNextPageParam: lastPage => {
        if (
          lastPage.data?.meta?.current_page >= lastPage.data?.meta?.last_page
        ) {
          return false;
        }
        return lastPage.data?.meta?.current_page + 1;
      },
      refetchOnWindowFocus: false,
      enabled: isStaff,
    },
  );

  const responsibleOptions =
    responsibleUsersQuery.data?.pages
      ?.reduce((data, page) => [...data, ...page.data.data], [])
      .map(option => ({ value: option.id, label: option.name })) ?? [];

  const methods = useForm({
    defaultValues,
    mode: "all",
    resolver: yupResolver(combinedSchema),
  });

  const { mutate: saveOnboarding, isLoading: isSaving } = useSaveOnboarding({
    onSuccess: () => {
      toast.success("Dados de onboarding salvos com sucesso!");
      handleCloseDialog();
    },
    onError: error => {
      toast.error("Erro ao salvar dados de onboarding.");
      console.error("Error saving onboarding data", error);
    },
  });

  const { mutate: sendOnboarding, isLoading: isSending } = useSendOnboarding({
    onSuccess: () => {
      toast.success("Onboarding enviado com sucesso!");
      handleCloseDialog();
    },
    onError: error => {
      toast.error("Erro ao enviar onboarding.");
      console.error("Error sending onboarding data", error);
    },
  });

  const {
    mutate: returnValidationOnboarding,
    isLoading: isReturning,
  } = useReturnValidationOnboarding({
    onSuccess: () => {
      toast.success("Onboarding devolvido com sucesso!");
      setIsReturnDialogOpen(false);
      handleCloseDialog();
    },
    onError: error => {
      toast.error("Erro ao devolver onboarding.");
      console.error("Error returning onboarding", error);
    },
  });

  const {
    mutate: closeVersionOnboarding,
    isLoading: isClosingVersion,
  } = useCloseVersionOnboarding({
    onSuccess: () => {
      toast.success("Versão fechada com sucesso!");
      setIsCloseVersionDialogOpen(false);
      handleCloseDialog();
    },
    onError: error => {
      toast.error("Erro ao fechar versão.");
      console.error("Error closing onboarding version", error);
    },
  });

  const { data: onboardingData, isLoading: isFetching } = useGetOnboarding(
    companyCode,
  );

  const isPendingValidation = onboardingData?.pendingValidation === true;
  const isFormLocked =
    (isPendingValidation && !isStaff) || onboardingData?.editable === false;

  const isValidated =
    methods.watch("socialDataAndChargesIsValidated") &&
    methods.watch("admissionIsValidated") &&
    methods.watch("payrollIsValidated") &&
    methods.watch("bankCommunicationIsValidated") &&
    methods.watch("businessRuleIsValidated") &&
    methods.watch("benefitsIsValidated");

  useEffect(() => {
    if (onboardingData?.formData) {
      let parsedData = {};
      try {
        parsedData =
          typeof onboardingData.formData === "string"
            ? JSON.parse(onboardingData.formData)
            : onboardingData.formData;
      } catch (e) {
        console.error("Failed to parse formData", e);
      }
      methods.reset({ ...defaultValues, ...parsedData });
    }
  }, [onboardingData, methods]);

  useEffect(() => {
    if (onboardingData?.responsibleUser) {
      setResponsibleUser({
        value: onboardingData.responsibleUser.id,
        label: onboardingData.responsibleUser.name,
      });
    } else {
      setResponsibleUser(null);
    }
    setChatEnabled(onboardingData?.chatEnabled === true);
    setEditable(onboardingData?.editable === true);
  }, [onboardingData]);

  const handleCloseDialog = () => {
    searchParams.delete("screen");
    setSearchParams(searchParams);
  };

  const handleDevFill = () => {
    methods.reset(demoValues);
  };

  const handleSave = () => {
    setSchemaErrors([]);
    const formData = methods.getValues();

    saveOnboarding({
      companyCode: companyCode,
      formData: formData,
      ...(isStaff && {
        responsibleUserId: responsibleUser?.value ?? null,
        chatEnabled,
        editable,
      }),
    });
  };

  const handleSend = async () => {
      
    const isValid = await methods.trigger();

    if (!isValid) {
      const fieldErrors = methods.formState.errors;
      const collectMessages = (node, acc = []) => {
        if (!node || typeof node !== "object") return acc;
        if (typeof node.message === "string" && node.message !== "") {
          acc.push(node.message);
        }
        Object.values(node).forEach(value => {
          if (value && typeof value === "object" && !value.nodeType) {
            collectMessages(value, acc);
          }
        });
        return acc;
      };
      const messages = collectMessages(fieldErrors);
      setSchemaErrors(messages);
      return;
    }

    setSchemaErrors([]);
    const formData = methods.getValues();

    sendOnboarding({
      companyCode: companyCode,
      formData: formData,
      ...(isStaff && { responsibleUserId: responsibleUser?.value ?? null }),
    });
  };

  const handleConfirmReturn = () => {
    returnValidationOnboarding({ companyCode });
  };

  const handleConfirmCloseVersion = () => {
    closeVersionOnboarding({ companyCode });
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        fullScreen
        open
        onClose={handleCloseDialog}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Onboarding
              {onboardingData?.version && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 2, opacity: 0.8 }}
                >
                  (Versão: {onboardingData.version})
                </Typography>
              )}
            </Typography>
            {!isPendingValidation ? (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  color="inherit"
                  variant="outlined"
                  onClick={handleSave}
                  disabled={isSaving || isSending || isFetching}
                >
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleSend}
                  disabled={isSaving || isSending || isFetching}
                >
                  {isSending ? "Enviando..." : "Enviar"}
                </Button>
              </Box>
            ) : (
              isStaff &&
              (isValidated && isPendingValidation ? (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => setIsCloseVersionDialogOpen(true)}
                  disabled={isClosingVersion}
                >
                  {isClosingVersion ? "Fechando..." : "Fechar versão"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#d32f2f" }}
                  onClick={() => setIsReturnDialogOpen(true)}
                  disabled={isReturning}
                >
                  {isReturning ? "Devolvendo..." : "Devolver"}
                </Button>
              ))
            )}
          </Toolbar>
        </AppBar>

        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            py: 3,
            textAlign: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {isStaff && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: "10px",
              }}
            >
              <Box sx={{ flex: 1, maxWidth: 400 }}>
                <Autocomplete
                  value={responsibleUser}
                  onChange={value => setResponsibleUser(value)}
                  onInputChange={value => setResponsibleQuery(value)}
                  options={responsibleOptions}
                  loading={responsibleUsersQuery.isLoading}
                  infinite={{
                    fetchNextPage: responsibleUsersQuery.fetchNextPage,
                    hasNextPage: responsibleUsersQuery.hasNextPage,
                  }}
                  inputprops={{
                    label: "Analista responsável",
                    fullWidth: true,
                    variant: "outlined",
                    margin: "dense",
                    size: "small",
                  }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editable}
                    onChange={e => setEditable(e.target.checked)}
                    size="small"
                  />
                }
                label="Habilitar edição"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={chatEnabled}
                    onChange={e => setChatEnabled(e.target.checked)}
                    size="small"
                  />
                }
                label="Habilitar chat"
              />
            </Box>
          )}

          {isPendingValidation ? (
            isStaff ? (
              <Alert severity="warning" sx={{ mx: "auto", textAlign: "left" }}>
                <AlertTitle>Formulário pendente de validação</AlertTitle>
                Este formulário foi enviado e está aguardando validação. Revise
                os campos de cada seção e, ao concluir, marque o checkbox{" "}
                <strong>&quot;Validado&quot;</strong> no canto superior direito
                das seções para confirmar a validação.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mx: "auto", textAlign: "left" }}>
                <AlertTitle>Formulário em validação</AlertTitle>
                Seu formulário foi enviado e está em processo de validação. Em
                breve ele será revisado por um analista. Aguarde a conclusão da
                análise para que os campos sejam liberados novamente.
              </Alert>
            )
          ) : (
            <Alert severity="info" sx={{ mx: "auto", textAlign: "left" }}>
              <AlertTitle>Bem-vindo ao processo de Onboarding</AlertTitle>
              Preencha as informações da empresa navegando pelas abas abaixo.
              Você pode clicar em <strong>Salvar</strong> a qualquer momento
              para não perder seu progresso (sem validação obrigatória). Ao
              finalizar o preenchimento de todos os dados obrigatórios, clique
              em <strong>Enviar</strong> para concluir o onboarding de forma
              definitiva.
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            centered
            sx={{ mt: 2 }}
          >
            {tabs.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Box>

        <DialogContent sx={{ p: { xs: 1, sm: 2 }, flex: 1 }}>
          {schemaErrors.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setSchemaErrors([])}
            >
              <AlertTitle>Corrija os campos antes de salvar</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {schemaErrors.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </Alert>
          )}
          <fieldset
            disabled={isFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minInlineSize: 0 }}
          >
            {
              [
                <SocialDataAndCharges isStaff={isStaff} />,
                <Admission isStaff={isStaff} />,
                <Payroll isStaff={isStaff} />,
                <BankCommunication isStaff={isStaff} />,
                <BusinessRule isStaff={isStaff} />,
                <Benefits isStaff={isStaff} />,
              ][activeTab]
            }
          </fieldset>
        </DialogContent>

          </Box>

          {chatEnabled && (
            <Drawer
              variant="permanent"
              anchor="right"
              sx={{
                width: 380,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                  width: 380,
                  boxSizing: "border-box",
                  position: "relative",
                  overflow: "hidden",
                },
              }}
            >
              <OnboardingChat
                companyCode={companyCode}
                currentUserId={user?.id}
                enabled={!!onboardingData?.id}
              />
            </Drawer>
          )}
        </Box>
        {APP_STAGE === "development" && (
          <Fab
            color="primary"
            aria-label="add"
            style={{
              position: "fixed",
              bottom: "16px",
              right: "16px",
              backgroundColor: "red",
            }}
            onClick={handleDevFill}
          >
            <DeveloperMode />
          </Fab>
        )}
        <Dialog
          open={isReturnDialogOpen}
          onClose={() => setIsReturnDialogOpen(false)}
        >
          <DialogTitle>Devolver onboarding</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja devolver este onboarding? O formulário
              será liberado para o cliente editar novamente.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsReturnDialogOpen(false)}
              disabled={isReturning}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmReturn}
              disabled={isReturning}
              sx={{ backgroundColor: "#d32f2f" }}
            >
              {isReturning ? "Devolvendo..." : "Devolver"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={isCloseVersionDialogOpen}
          onClose={() => setIsCloseVersionDialogOpen(false)}
        >
          <DialogTitle>Fechar versão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja fechar esta versão do onboarding? Uma nova
              versão será criada e o formulário atual não poderá mais ser
              editado.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsCloseVersionDialogOpen(false)}
              disabled={isClosingVersion}
            >
              Cancelar
            </Button>
            <Button
              color="success"
              variant="contained"
              onClick={handleConfirmCloseVersion}
              disabled={isClosingVersion}
            >
              {isClosingVersion ? "Fechando..." : "Fechar versão"}
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </FormProvider>
  );
}
